import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';
import {
  buildEmptyIpr,
  computeIprTotal,
  countContactsWithIpr,
  IPR_TOOL_SELECT,
  normalizeIprData,
  toggleToothMarker,
  updateIprContact,
} from '../../../lib/iprToothData';
import IprSheetBody from '../../ipr-stripping-sheet/components/IprSheetBody';

/**
 * Modal jalon : même fiche IPR que la page /ipr-stripping-sheet
 * (diagramme + marqueurs bouton/attachements/… + table).
 * À la sauvegarde : upsert + validation du jalon.
 */
const IPRMilestoneModal = ({ isOpen, onClose, caseId, milestoneId, onSaved }) => {
  const [iprData, setIprData] = useState(buildEmptyIpr);
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [activeTool, setActiveTool] = useState(IPR_TOOL_SELECT);
  const [sheetId, setSheetId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !caseId) return;

    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('ipr_stripping_sheets')
          .select('id, ipr_data')
          .eq('case_id', caseId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setSheetId(data.id);
          setIprData(normalizeIprData(data.ipr_data));
        } else {
          setSheetId(null);
          setIprData(buildEmptyIpr());
        }
      } catch (err) {
        console.error('[IPRMilestoneModal] load error:', err);
        toast.error('Erreur lors du chargement de la fiche IPR : ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, caseId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTooth(null);
      setActiveTool(IPR_TOOL_SELECT);
    }
  }, [isOpen]);

  const handleIPRUpdate = useCallback((contactId, value) => {
    setIprData((prev) => updateIprContact(prev, contactId, value));
  }, []);

  const handleToggleMarker = useCallback((toothNumber, markerKey) => {
    setIprData((prev) => toggleToothMarker(prev, toothNumber, markerKey));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      const total = parseFloat(computeIprTotal(iprData));

      if (sheetId) {
        const { error } = await supabase
          .from('ipr_stripping_sheets')
          .update({
            ipr_data: iprData,
            total_ipr_mm: total,
            updated_by: user?.id || null,
            updated_at: now,
          })
          .eq('id', sheetId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('ipr_stripping_sheets')
          .insert({
            case_id: caseId,
            ipr_data: iprData,
            total_ipr_mm: total,
            created_by: user?.id || null,
            updated_by: user?.id || null,
          })
          .select('id')
          .single();
        if (error) throw error;
        setSheetId(data.id);
      }

      if (milestoneId) {
        const { error: msError } = await supabase
          .from('phase_milestones')
          .update({ is_completed: true, completed_date: now, updated_at: now })
          .eq('id', milestoneId);
        if (msError) {
          console.warn('[IPRMilestoneModal] milestone update failed:', msError.message);
          toast.warning(
            "Fiche IPR enregistrée, mais le jalon n'a pas pu être validé automatiquement."
          );
        }
      }

      toast.success('Fiche IPR enregistrée et jalon validé !');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('[IPRMilestoneModal] save error:', err);
      toast.error("Erreur lors de l'enregistrement : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const totalIPR = computeIprTotal(iprData);
  const contactsWithIPR = countContactsWithIpr(iprData);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col border border-border">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/20 flex-shrink-0 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Scissors" size={17} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">Fiche de Stripping (IPR)</h2>
              <p className="text-xs text-muted-foreground truncate">
                Même fiche clinique — contacts IPR, boutons, attachements, crochets
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-center px-3 py-1.5 bg-primary/10 rounded-lg">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                Total IPR
              </p>
              <p className="text-sm font-bold text-primary">{totalIPR} mm</p>
            </div>
            <div className="text-center px-3 py-1.5 bg-muted/50 rounded-lg">
              <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                Contacts
              </p>
              <p className="text-sm font-bold text-foreground">{contactsWithIPR}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <svg
                  className="animate-spin h-8 w-8 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                <p className="text-sm">Chargement de la fiche IPR...</p>
              </div>
            </div>
          ) : (
            <IprSheetBody
              iprData={iprData}
              selectedTooth={selectedTooth}
              onToothSelect={setSelectedTooth}
              activeTool={activeTool}
              onToolChange={setActiveTool}
              onToggleMarker={handleToggleMarker}
              onIPRUpdate={handleIPRUpdate}
              showPatientHeader={false}
              showClinicalNotes
            />
          )}
        </div>

        {!loading && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-border bg-muted/10 flex-shrink-0 gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              <Icon name="Info" size={13} className="flex-shrink-0" />
              <span className="truncate">
                {sheetId
                  ? 'Une fiche IPR existe déjà pour ce cas — elle sera mise à jour.'
                  : 'Aucune fiche IPR existante — une nouvelle fiche sera créée.'}
              </span>
            </div>
            <div className="flex gap-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground
                  hover:bg-primary/90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {saving ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      />
                    </svg>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={15} />
                    Enregistrer et valider le jalon
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IPRMilestoneModal;
